import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Bell, Settings, Moon, Brain, Battery, Shield, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';

interface Notification {
  id: string;
  type: 'energy' | 'cycle' | 'rest' | 'sleep';
  title: string;
  message: string;
  timestamp: Date;
  priority: 'low' | 'medium' | 'high';
}

type Mode = 'focus' | 'immersion' | 'emergency' | null;

export function NotificationCenter() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [mode, setMode] = useState<Mode>(null);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (!currentUser) return;

    const loadNotifications = async () => {
      try {
        const { data, error } = await supabase
          .from('watch_notifications')
          .select('*')
          .eq('user_id', currentUser.id)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setNotifications(data || []);
      } catch (error) {
        console.error('Error loading notifications:', error);
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Impossible de charger les notifications.",
        });
      } finally {
        setLoading(false);
      }
    };

    loadNotifications();

    // Subscribe to real-time notifications
    const subscription = supabase
      .channel('watch_notifications')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'watch_notifications',
          filter: `user_id=eq.${currentUser.id}`
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setNotifications(prev => [payload.new as any, ...prev]);
          } else if (payload.eventType === 'DELETE') {
            setNotifications(prev => prev.filter(n => n.id !== payload.old.id));
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [currentUser, toast]);

  const handleModeChange = async (newMode: Mode) => {
    if (mode === newMode) {
      setMode(null);
      return;
    }
    setMode(newMode);

    if (!currentUser) return;

    try {
      // Update user's active mode
      const { error } = await supabase
        .from('watch_modes')
        .insert({
          user_id: currentUser.id,
          mode: newMode,
          started_at: new Date().toISOString()
        });

      if (error) throw error;

      toast({
        title: "Mode activé",
        description: `Mode ${newMode} activé avec succès.`,
      });
    } catch (error) {
      console.error('Error changing mode:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de changer de mode.",
      });
    }
  };

  const getFilteredNotifications = () => {
    if (!mode) return notifications;

    const modeFilters = {
      focus: ['cycle', 'energy'],
      immersion: ['energy'],
      emergency: ['energy', 'rest', 'sleep']
    };

    return notifications.filter(notification => 
      modeFilters[mode]?.includes(notification.type)
    );
  };

  const dismissNotification = async (id: string) => {
    try {
      const { error } = await supabase
        .from('watch_notifications')
        .update({ read: true })
        .eq('id', id);

      if (error) throw error;
      
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (error) {
      console.error('Error dismissing notification:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de supprimer la notification.",
      });
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'energy':
        return <Battery className="w-5 h-5" />;
      case 'cycle':
        return <Brain className="w-5 h-5" />;
      case 'rest':
        return <Shield className="w-5 h-5" />;
      case 'sleep':
        return <Moon className="w-5 h-5" />;
      default:
        return <Bell className="w-5 h-5" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-orange-100 text-orange-800';
      case 'low':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div className="flex items-center gap-2">
          <Bell className="w-6 h-6 text-blue-600" />
          <h2 className="text-xl font-bold">Centre de notifications</h2>
        </div>
        <Button
          variant="outline"
          size="icon"
          onClick={() => {}}
          className="h-8 w-8"
        >
          <Settings className="w-4 h-4" />
        </Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Mode Selection */}
          <div className="flex gap-2">
            <Button
              variant={mode === 'focus' ? 'default' : 'outline'}
              onClick={() => handleModeChange('focus')}
              className="flex-1"
            >
              <Brain className="w-4 h-4 mr-2" />
              Focus
            </Button>
            <Button
              variant={mode === 'immersion' ? 'default' : 'outline'}
              onClick={() => handleModeChange('immersion')}
              className="flex-1"
            >
              <Shield className="w-4 h-4 mr-2" />
              Immersion
            </Button>
            <Button
              variant={mode === 'emergency' ? 'destructive' : 'outline'}
              onClick={() => handleModeChange('emergency')}
              className="flex-1"
            >
              <Bell className="w-4 h-4 mr-2" />
              Urgence
            </Button>
          </div>

          {/* Notifications List */}
          <div className="space-y-4">
            <AnimatePresence>
              {getFilteredNotifications().map((notification) => (
                <motion.div
                  key={notification.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  className={`p-4 rounded-lg border ${
                    notification.priority === 'high' 
                      ? 'border-red-200 bg-red-50' 
                      : notification.priority === 'medium'
                        ? 'border-orange-200 bg-orange-50'
                        : 'border-blue-200 bg-blue-50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${getPriorityColor(notification.priority)}`}>
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div>
                        <h3 className="font-medium">{notification.title}</h3>
                        <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                        <span className="text-xs text-gray-500 mt-2 block">
                          {new Date(notification.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => dismissNotification(notification.id)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {getFilteredNotifications().length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Bell className="w-8 h-8 mx-auto mb-3 text-gray-400" />
                <p>Aucune notification pour le moment</p>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}